"use client";

import { useEffect, useRef, useState } from "react";
import {
  ABILITY_CATEGORY_WHEEL,
  ABILITY_CATEGORY_WHEELS,
  ABILITY_COUNT_WHEEL,
  ALIGNMENT_WHEEL,
  CONSOLATION_GATE_WHEEL,
  CROWN_STAT_MAP,
  CROWN_THRESHOLD,
  CROWN_WHEEL,
  FIGHT_OPPONENT_WHEEL,
  FIGHT_OUTCOME_WHEEL,
  POWER_MASTERY_WHEEL,
  SIDEKICK_OF_WHEEL,
  WEAPON_ABILITIES_WHEEL,
  WHEELS,
  Wheel,
  WheelOption,
  getAbilityCount,
} from "@/data/wheels";
import {
  Alignment,
  calculateRank,
  calculateTotalScore,
  getLeaderboardBoard,
  spinWeightedWheel,
  spinWheel,
} from "@/lib/rng";
import { SpinResult } from "@/components/ResultsSummary";
import {
  ActiveRunState,
  DEMO_KEY,
  MATCH_KEY,
  clearActiveRun,
  hasRunProgress,
  loadActiveRun,
  migrateGuestRunToAccount,
  migrateLegacyRun,
  saveActiveRun,
  soloRunKey,
} from "@/lib/runPersistence";
import {
  clearActiveRunRemote,
  loadActiveRunRemote,
  saveActiveRunRemote,
} from "@/lib/activeRunRemote";
import { useAuth } from "@/hooks/useAuth";
import { MAX_SINGLE_WHEEL_REROLLS } from "@/lib/tokens";
import {
  ChaosGameState,
  ChaosOutcome,
  ChaosSpinVisual,
  applyAbilityHalving,
  applyChaosOutcomeEffects,
  applyPendingSpinEffects,
  canManuallyTriggerChaos,
  createInitialChaosState,
  finalizePendingDouble,
  getChaosCooldownLabel,
  isAbilityCategorySkipped,
  recordAbilityResult,
  recordPowerMastery,
  recordWeakness,
  recordWheelResult,
  rollChaosTrigger,
  triggerChaosEvent,
} from "@/lib/chaosEngine";

export type GamePhase =
  | "alignment"
  | "sidekickOf"
  | "main"
  | "abilities"
  | "fight"
  | "complete";
export type ChaosOverlayStage = "type" | "result" | null;
export type AbilityStep =
  | "count"
  | "category"
  | "ability"
  | "consolationGate"
  | "consolationWeapon"
  | "mastery";
export type FightStep = "opponent" | "outcome" | null;
export type CrownStage = "choice" | "spinning" | "result" | null;

// `demo` and `match` both run the full game (identical wheels, RNG, chaos,
// scoring) but against a SEPARATE localStorage key with NO Supabase/remote
// active-run sync — so they can never overwrite the real solo run or sync to
// the cloud. (A finished match is saved to the profile separately, in page.tsx.)
export function useGameState(options?: { demo?: boolean; match?: boolean }) {
  const demo = options?.demo ?? false;
  const match = options?.match ?? false;
  // Ephemeral runs (demo / match) use their own fixed slot and skip all remote
  // sync + the restore toast.
  const ephemeral = demo || match;
  const fixedKey = demo ? DEMO_KEY : match ? MATCH_KEY : null;
  const { user, profile, loading: authLoading } = useAuth();

  // For a SOLO run the storage slot follows the player's identity (a shared
  // guest slot, or one slot per signed-in account) so two accounts on the same
  // device never share a run. `loadedId` is the identity whose run is currently
  // in memory; SAVES target its key (not the live `user`), so an identity change
  // can't write the previous account's in-memory state into the new one's slot.
  // `undefined` = the solo run hasn't been loaded for any identity yet.
  const [loadedId, setLoadedId] = useState<string | null | undefined>(
    ephemeral ? null : undefined,
  );
  const loadedIdRef = useRef<string | null | undefined>(
    ephemeral ? null : undefined,
  );
  // The uid we've already attempted the one-time guest→account carry for, so it
  // runs at most once per account even as `profile` settles in after sign-in.
  const carriedForRef = useRef<string | null>(null);
  const saveKey = fixedKey ?? soloRunKey(loadedId);

  // Restores an in-progress (or finished-but-unsubmitted) run on load, so the
  // player lands back exactly where they left off after a refresh, back
  // navigation, accidental tab close, or full browser close. Reading
  // localStorage must happen post-mount (not in a lazy initializer) so the
  // initial client render matches the server-rendered HTML.
  const [hydrated, setHydrated] = useState(false);
  const [showRestoredToast, setShowRestoredToast] = useState(false);
  const initialRunRef = useRef<ActiveRunState | null>(null);
  // Throttles remote sync (every 3rd spin) and ensures the cross-device cloud
  // restore runs once per identity. Declared up here so the identity-load effect
  // and resetState (both above their original spots) can reference them.
  const remoteSyncCount = useRef(0);
  const remoteCheckedRef = useRef(false);

  const [phase, setPhase] = useState<GamePhase>("alignment");
  const [alignment, setAlignment] = useState<Alignment | null>(null);
  const [sidekickOf, setSidekickOf] = useState<Alignment | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState<SpinResult[]>([]);
  const [pendingOption, setPendingOption] = useState<WheelOption | null>(null);
  const [spinning, setSpinning] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [spinToken, setSpinToken] = useState(0);

  // Ability sub-flow: count -> (category -> ability) * count -> mastery.
  const [abilityStep, setAbilityStep] = useState<AbilityStep>("count");
  const [abilityCount, setAbilityCount] = useState(0);
  const [abilitiesCompleted, setAbilitiesCompleted] = useState(0);
  const [currentAbilityCategory, setCurrentAbilityCategory] = useState<
    string | null
  >(null);

  // Optional bonus round, triggered from the results screen.
  const [fightStep, setFightStep] = useState<FightStep>(null);
  const [fightResults, setFightResults] = useState<SpinResult[]>([]);

  // Crown Mechanic: triggered once if the final score hits CROWN_THRESHOLD.
  const [hasCrown, setHasCrown] = useState(false);
  const [crownStage, setCrownStage] = useState<CrownStage>(null);
  const [crownSpinToken, setCrownSpinToken] = useState(0);
  const [crownPendingOption, setCrownPendingOption] = useState<WheelOption | null>(null);
  const [crownBonusOption, setCrownBonusOption] = useState<WheelOption | null>(null);

  // When the current run first reached the results screen. Drives the reroll
  // cooldown (see ResultsSummary). Persisted so a refresh resumes the same
  // countdown; cleared by reset() so a fresh run starts with no cooldown.
  const [cooldownStartedAt, setCooldownStartedAt] = useState<number | null>(
    null,
  );

  // The leaderboard id once this run has been submitted (null until then).
  // Persisted so a submitted run survives navigation instead of being wiped.
  const [submittedScoreId, setSubmittedScoreId] = useState<string | null>(null);

  // Paid single-wheel rerolls spent on this run, capped at
  // MAX_SINGLE_WHEEL_REROLLS. Persisted so the cap survives a refresh.
  const [wheelRerollsUsed, setWheelRerollsUsed] = useState(0);

  const [chaosResults, setChaosResults] = useState<SpinResult[]>([]);
  // Tracks every wheel/ability/weakness result + chaos flags so the chaos
  // engine can react to the player's actual run state.
  const [chaosGameState, setChaosGameState] = useState<ChaosGameState>(
    createInitialChaosState(),
  );
  const [spinsSinceLastChaos, setSpinsSinceLastChaos] = useState(0);
  // The currently-displayed chaos event overlay, if any.
  const [chaosOutcome, setChaosOutcome] = useState<ChaosOutcome | null>(null);
  const [chaosOverlayStage, setChaosOverlayStage] = useState<ChaosOverlayStage>(null);
  const [chaosTypeSpinToken, setChaosTypeSpinToken] = useState(0);
  const [chaosResultSpinToken, setChaosResultSpinToken] = useState(0);
  // True for the 3s gap between a main-wheel result landing and an
  // auto-triggered chaos event appearing, so the player sees that wheel's
  // own score first.
  const [chaosPending, setChaosPending] = useState(false);
  const chaosTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const mainWheel = WHEELS[currentIndex];

  const abilityWheel: Wheel =
    abilityStep === "count"
      ? ABILITY_COUNT_WHEEL
      : abilityStep === "category"
        ? ABILITY_CATEGORY_WHEEL
        : abilityStep === "ability"
          ? ABILITY_CATEGORY_WHEELS[currentAbilityCategory ?? "Physical"]
          : abilityStep === "consolationGate"
            ? CONSOLATION_GATE_WHEEL
            : abilityStep === "consolationWeapon"
              ? WEAPON_ABILITIES_WHEEL
              : POWER_MASTERY_WHEEL;

  const fightWheel: Wheel =
    fightStep === "outcome" ? FIGHT_OUTCOME_WHEEL : FIGHT_OPPONENT_WHEEL;

  const currentWheel: Wheel | null =
    phase === "alignment"
      ? ALIGNMENT_WHEEL
      : phase === "sidekickOf"
        ? SIDEKICK_OF_WHEEL
        : phase === "main"
          ? mainWheel
          : phase === "abilities"
            ? abilityWheel
            : phase === "fight"
              ? fightWheel
              : null;

  // Cosmetic spins (alignment, sidekick, ability category, the consolation
  // gate) carry no points.
  const isAbilityCategoryStep = phase === "abilities" && abilityStep === "category";
  const showValue =
    phase === "main" ||
    (phase === "abilities" &&
      abilityStep !== "category" &&
      abilityStep !== "consolationGate");

  const spin = () => {
    if (spinning || showResult || chaosOverlayStage || chaosPending || !currentWheel) return;
    const option = isAbilityCategoryStep
      ? spinWeightedWheel(currentWheel)
      : spinWheel(currentWheel);
    setPendingOption(option);
    setShowResult(false);
    setSpinning(true);
    setSpinToken((t) => t + 1);
  };

  // Resolves a full chaos event (Chaos Type -> Buff/Nerf/Lore/Achievement)
  // and opens the overlay (starting with the "type" reveal), but does NOT
  // apply its score/result changes yet — those land only once the overlay
  // is dismissed, so the player sees the score change happen right in front
  // of them. `gameState` reflects the run as of the spin that just landed
  // (which may not be in `chaosGameState` yet at call time).
  const runChaosEvent = (gameState: ChaosGameState, triggeredAfterWheel: string) => {
    const outcome = triggerChaosEvent(gameState, triggeredAfterWheel);
    setChaosOutcome(outcome);
    setChaosOverlayStage("type");
    setChaosTypeSpinToken((t) => t + 1);
    setSpinsSinceLastChaos(0);
  };

  const triggerChaosManually = () => {
    if (
      spinning ||
      showResult ||
      chaosOverlayStage ||
      chaosPending ||
      !canManuallyTriggerChaos(spinsSinceLastChaos)
    )
      return;
    runChaosEvent(chaosGameState, currentWheel?.name ?? "Manual");
  };

  // "Chaos Type" reveal finished -> drop into the specific result wheel.
  const handleChaosTypeSpinEnd = () => {
    setChaosOverlayStage("result");
    setChaosResultSpinToken((t) => t + 1);
  };

  // Specific result wheel finished spinning -> the detail card (with the
  // dismiss button) is now shown by the overlay itself.
  const handleChaosResultSpinEnd = () => {};

  // Applies the resolved chaos outcome now that the player has seen it: the
  // affected wheel/ability result updates in place (or, for "new" effects, a
  // line is added to the chaos log), the score recalculates, and the overlay
  // closes.
  const dismissChaosOverlay = () => {
    if (chaosOutcome) {
      const { results: newResults, chaosResults: newChaosResults } =
        applyChaosOutcomeEffects(chaosOutcome, results, chaosResults);
      setResults(newResults);
      setChaosResults(newChaosResults);
      setChaosGameState(chaosOutcome.state);
    }
    setChaosOutcome(null);
    setChaosOverlayStage(null);
  };

  const chaosCooldownLabel = getChaosCooldownLabel(spinsSinceLastChaos);
  const canTriggerChaos = canManuallyTriggerChaos(spinsSinceLastChaos);

  // Applies a saved snapshot (local or remote) to every piece of run state.
  const applyRunState = (run: ActiveRunState) => {
    setPhase(run.phase);
    setAlignment(run.alignment);
    setSidekickOf(run.sidekickOf);
    setCurrentIndex(run.currentIndex);
    setResults(run.results);
    setPendingOption(run.pendingOption);
    setShowResult(run.showResult);
    setAbilityStep(run.abilityStep);
    setAbilityCount(run.abilityCount);
    setAbilitiesCompleted(run.abilitiesCompleted);
    setCurrentAbilityCategory(run.currentAbilityCategory);
    setFightStep(run.fightStep);
    setFightResults(run.fightResults);
    setHasCrown(run.hasCrown);
    setCrownStage(run.crownStage);
    setCrownBonusOption(run.crownBonusOption);
    setChaosResults(run.chaosResults);
    setChaosGameState(run.chaosGameState);
    setSpinsSinceLastChaos(run.spinsSinceLastChaos);
    setCooldownStartedAt(run.cooldownStartedAt ?? null);
    setSubmittedScoreId(run.submittedScoreId ?? null);
    setWheelRerollsUsed(run.wheelRerollsUsed ?? 0);
  };

  // Resets every piece of run state back to a brand-new game, WITHOUT touching
  // localStorage/remote (callers decide whether to also clear storage). Used both
  // by reset() and when switching into an identity that has no saved run, so the
  // previous identity's run never lingers on screen.
  const resetState = () => {
    setPhase("alignment");
    setAlignment(null);
    setSidekickOf(null);
    setCurrentIndex(0);
    setResults([]);
    setPendingOption(null);
    setSpinning(false);
    setShowResult(false);
    setSpinToken(0);
    setAbilityStep("count");
    setAbilityCount(0);
    setAbilitiesCompleted(0);
    setCurrentAbilityCategory(null);
    setFightStep(null);
    setFightResults([]);
    setChaosResults([]);
    setChaosGameState(createInitialChaosState());
    setSpinsSinceLastChaos(0);
    setCooldownStartedAt(null);
    setSubmittedScoreId(null);
    setWheelRerollsUsed(0);
    setChaosOutcome(null);
    setChaosOverlayStage(null);
    if (chaosTimeoutRef.current) clearTimeout(chaosTimeoutRef.current);
    setChaosPending(false);
    setHasCrown(false);
    setCrownStage(null);
    setCrownSpinToken(0);
    setCrownPendingOption(null);
    setCrownBonusOption(null);
    remoteSyncCount.current = 0;
  };

  // Loads the right run for the current identity (post-mount, so the client's
  // first paint matches the server HTML). For ephemeral runs this is a one-shot
  // read of the fixed slot. For solo runs it waits for auth to settle, then loads
  // that identity's slot — and re-runs when the identity changes (sign in / out /
  // switch account) so each account always sees ITS OWN run, never the last one's.
  useEffect(() => {
    if (ephemeral) {
      if (hydrated) return;
      const run = loadActiveRun(fixedKey!);
      initialRunRef.current = run;
      if (run) applyRunState(run);
      setHydrated(true);
      return;
    }

    // Wait until auth is resolved so we load the correct slot the first time,
    // instead of briefly showing the guest run before an account loads in.
    if (authLoading) return;
    const id = user?.id ?? null;
    const identityChanged = loadedIdRef.current !== id;
    // The guest→account carry can only be decided once the profile is known
    // (it hinges on signup_bonus_claimed); defer it until profile arrives.
    const needCarryCheck =
      id !== null && profile != null && carriedForRef.current !== id;
    if (!identityChanged && !needCarryCheck) return;

    const key = soloRunKey(id);
    if (identityChanged) migrateLegacyRun(key);

    // Fresh registration carries the guest's in-progress run into the new
    // account — the same one-time event as the guest-token migration. An
    // EXISTING account (signup bonus already claimed) keeps its own run.
    let carriedRun: ActiveRunState | null = null;
    if (needCarryCheck) {
      carriedForRef.current = id;
      if (profile && !profile.signup_bonus_claimed) {
        carriedRun = migrateGuestRunToAccount(id!);
        if (carriedRun) saveActiveRunRemote(id!, carriedRun).catch(() => {});
      }
    }

    if (identityChanged) {
      const run = carriedRun ?? loadActiveRun(key);
      initialRunRef.current = run;
      if (run) {
        applyRunState(run);
        if (hasRunProgress(run)) setShowRestoredToast(true);
      } else if (loadedIdRef.current !== undefined) {
        // Switched into an identity with no saved run → start clean rather than
        // leaving the previous identity's run on screen.
        resetState();
      }
      remoteCheckedRef.current = false; // re-check the cloud for this identity
      loadedIdRef.current = id;
      setLoadedId(id);
      setHydrated(true);
    } else if (carriedRun) {
      // Account slot was already loaded (empty) before the profile arrived;
      // now that the carry happened, surface the guest run.
      initialRunRef.current = carriedRun;
      applyRunState(carriedRun);
      if (hasRunProgress(carriedRun)) setShowRestoredToast(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ephemeral, authLoading, user, profile]);

  // Persist every change instantly to localStorage so the run survives a
  // refresh, back navigation, or closing the tab/browser entirely.
  useEffect(() => {
    if (!hydrated) return;
    const run: ActiveRunState = {
      version: 3,
      updatedAt: Date.now(),
      phase,
      alignment,
      sidekickOf,
      currentIndex,
      results,
      // Only persist a landed result once it's actually recorded and waiting
      // on "Next" - a spin still in flight has nothing worth resuming.
      pendingOption: showResult ? pendingOption : null,
      showResult,
      abilityStep,
      abilityCount,
      abilitiesCompleted,
      currentAbilityCategory,
      fightStep,
      fightResults,
      hasCrown,
      // A mid-spin crown wheel falls back to the choice screen so the player
      // can spin again; a result screen has already had its bonus recorded
      // into `results`, so it can just close.
      crownStage:
        crownStage === "spinning"
          ? "choice"
          : crownStage === "result"
            ? null
            : crownStage,
      crownBonusOption,
      chaosResults,
      chaosGameState,
      spinsSinceLastChaos,
      cooldownStartedAt,
      submittedScoreId,
      wheelRerollsUsed,
    };
    // Always save to the LOADED identity's slot, never the live `user`'s — so
    // during a sign-in/out/switch the previous account's in-memory state lands
    // in its own slot, not the new account's.
    saveActiveRun(run, saveKey);

    // Ephemeral (demo / match) runs are never synced to Supabase. Only push to
    // the cloud once the loaded identity matches the signed-in user (i.e. not
    // mid-switch), so we never upload one account's run under another's id.
    const totalSpins = results.length + chaosResults.length + fightResults.length;
    if (
      !ephemeral &&
      user &&
      loadedId === user.id &&
      totalSpins > 0 &&
      totalSpins % 3 === 0 &&
      totalSpins !== remoteSyncCount.current
    ) {
      remoteSyncCount.current = totalSpins;
      saveActiveRunRemote(user.id, run).catch(() => {});
    }
  }, [
    ephemeral,
    saveKey,
    loadedId,
    hydrated,
    user,
    phase,
    alignment,
    sidekickOf,
    currentIndex,
    results,
    pendingOption,
    showResult,
    abilityStep,
    abilityCount,
    abilitiesCompleted,
    currentAbilityCategory,
    fightStep,
    fightResults,
    hasCrown,
    crownStage,
    crownBonusOption,
    chaosResults,
    chaosGameState,
    spinsSinceLastChaos,
    cooldownStartedAt,
    submittedScoreId,
    wheelRerollsUsed,
  ]);

  // On login, if Supabase has a newer/active run than what's stored locally
  // (e.g. the player switched devices), restore from there instead.
  useEffect(() => {
    if (ephemeral || !hydrated || !user || remoteCheckedRef.current) return;
    remoteCheckedRef.current = true;
    (async () => {
      const remote = await loadActiveRunRemote(user.id);
      if (!remote || !hasRunProgress(remote)) return;
      const initialRun = initialRunRef.current;
      const localIsStale =
        !initialRun ||
        !hasRunProgress(initialRun) ||
        (initialRun.updatedAt ?? 0) < remote.updatedAt;
      if (!localIsStale) return;

      applyRunState(remote);
      setShowRestoredToast(true);
    })();
  }, [ephemeral, hydrated, user]);

  // Stamp the reroll cooldown the first time this run reaches the results
  // screen, exactly once. Guarded on the current value (not a mount ref) so a
  // refresh — which remounts everything but restores a non-null value — never
  // restarts the clock. Cleared only by reset().
  useEffect(() => {
    if (phase === "complete") {
      setCooldownStartedAt((prev) => prev ?? Date.now());
    }
  }, [phase]);

  const dismissRestoredToast = () => setShowRestoredToast(false);

  // Run submitted to the leaderboard. Record its id but KEEP the saved run, so
  // navigating to the leaderboard / tokens page and back returns the player to
  // their results (where they can still reroll or share) instead of wiping it.
  // The run is only cleared when a brand-new one starts (reset).
  const markSubmitted = (scoreId: string) => {
    setSubmittedScoreId(scoreId);
  };

  // Decorates a chaos-adjusted option's display name so the results list
  // shows what chaos did to it.
  const decorateOptionForVisual = (
    option: WheelOption,
    visual: ChaosSpinVisual,
  ): WheelOption => {
    switch (visual) {
      case "skipped":
        return { ...option, name: `${option.name} (Skipped by Chaos)` };
      case "halved":
        return { ...option, name: `${option.name} (Halved by Chaos)` };
      case "protected":
        return { ...option, name: `${option.name} (Protected by Chaos)` };
      case "evolved":
        return { ...option, name: `${option.name} (Evolved by Chaos)` };
      default:
        return option;
    }
  };

  const handleSpinEnd = () => {
    if (!pendingOption || !currentWheel) return;

    if (phase === "main") {
      const adjusted = applyPendingSpinEffects(
        chaosGameState,
        currentWheel.name,
        pendingOption,
      );
      const displayOption = decorateOptionForVisual(adjusted.option, adjusted.visual);
      const newResults = [
        ...results,
        { wheelName: currentWheel.name, option: displayOption },
      ];
      setResults(newResults);

      // Build on adjusted.state — it has the pending flag cleared and its event
      // resolved, so a fired-early buff/nerf stops showing as "Pending" once its
      // wheel lands.
      let newChaosState = recordWheelResult(
        adjusted.state,
        currentWheel.name,
        adjusted.option.name,
        adjusted.option.value,
      );
      if (currentWheel.name === "Weakness") {
        newChaosState = recordWeakness(
          newChaosState,
          adjusted.option.name,
          adjusted.option.value,
          "Main",
        );
      }

      setSpinsSinceLastChaos((s) => s + 1);

      if (rollChaosTrigger()) {
        // Let the player see this wheel's own result for a beat before the
        // chaos wheel takes over.
        setChaosPending(true);
        chaosTimeoutRef.current = setTimeout(() => {
          setChaosPending(false);
          runChaosEvent(newChaosState, currentWheel.name);
        }, 3000);
      } else {
        setChaosGameState(newChaosState);
      }
    } else if (phase === "abilities") {
      if (abilityStep === "count") {
        setAbilityCount(getAbilityCount(pendingOption.name));
        const points = applyAbilityHalving(chaosGameState, pendingOption.value);
        const displayOption: WheelOption = { name: pendingOption.name, value: points };
        setResults((prev) => [
          ...prev,
          { wheelName: "Abilities", option: displayOption },
        ]);
        setChaosGameState((prev) => ({
          ...prev,
          totalAbilityPoints: prev.totalAbilityPoints + points,
        }));
      } else if (abilityStep === "category") {
        setCurrentAbilityCategory(pendingOption.name);
      } else if (abilityStep === "ability") {
        const category = currentAbilityCategory ?? "Physical";
        const wheelName = `Ability: ${category}`;

        if (isAbilityCategorySkipped(chaosGameState, category)) {
          const displayOption: WheelOption = { name: "Taken by Chaos", value: 0 };
          setResults((prev) => [...prev, { wheelName, option: displayOption }]);
          setChaosGameState((prev) => ({
            ...prev,
            abilities: [
              ...prev.abilities,
              {
                category,
                label: displayOption.name,
                points: 0,
                lost: false,
                halved: false,
                source: "Main",
              },
            ],
            chaosFlags: {
              ...prev.chaosFlags,
              weaponSkipped:
                category === "Weapon" ? false : prev.chaosFlags.weaponSkipped,
              magicSkipped:
                category === "Magic" ? false : prev.chaosFlags.magicSkipped,
            },
          }));
        } else {
          const points = applyAbilityHalving(chaosGameState, pendingOption.value);
          const displayOption: WheelOption = { name: pendingOption.name, value: points };
          setResults((prev) => [...prev, { wheelName, option: displayOption }]);
          setChaosGameState((prev) =>
            recordAbilityResult(prev, category, pendingOption.name, pendingOption.value),
          );
        }
      } else if (abilityStep === "consolationWeapon") {
        setResults((prev) => [
          ...prev,
          {
            wheelName: "Consolation Weapon",
            option: { name: pendingOption.name, value: pendingOption.value * 2 },
          },
        ]);
      } else if (abilityStep === "mastery") {
        const points = applyAbilityHalving(chaosGameState, pendingOption.value);
        const displayOption: WheelOption = { name: pendingOption.name, value: points };
        setResults((prev) => [
          ...prev,
          { wheelName: "Power Mastery", option: displayOption },
        ]);
        setChaosGameState((prev) =>
          recordPowerMastery(prev, pendingOption.name, pendingOption.value),
        );
      }

      setSpinsSinceLastChaos((s) => s + 1);
    } else if (phase === "fight") {
      if (fightStep === "opponent") {
        setFightResults((prev) => [
          ...prev,
          { wheelName: "Opponent", option: pendingOption },
        ]);
      } else if (fightStep === "outcome") {
        setFightResults((prev) => [
          ...prev,
          { wheelName: "Outcome", option: pendingOption },
        ]);
      }
    } else if (phase === "alignment") {
      if (pendingOption.name === "Hero" || pendingOption.name === "Villain") {
        setAlignment(pendingOption.name);
      }
    } else if (phase === "sidekickOf") {
      setSidekickOf(pendingOption.name.startsWith("Hero") ? "Hero" : "Villain");
    }

    setSpinning(false);
    setShowResult(true);
  };

  const advanceMainIndex = () => {
    if (currentIndex + 1 >= WHEELS.length) {
      setPhase("abilities");
      setAbilityStep("count");
    } else {
      setCurrentIndex((i) => i + 1);
    }
  };

  // End of the ability sequence (either no abilities + declined the
  // consolation weapon, or after Power Mastery): resolves any pending
  // "Double Your Highest Stat" buff retroactively, then checks the crown.
  const finishAbilities = () => {
    const outcome = finalizePendingDouble(chaosGameState);
    if (outcome) {
      const { results: newResults, chaosResults: newChaosResults } =
        applyChaosOutcomeEffects(outcome, results, chaosResults);
      setResults(newResults);
      setChaosResults(newChaosResults);
      setChaosGameState(outcome.state);
    }
    if (totalScore >= CROWN_THRESHOLD) {
      setHasCrown(true);
      setCrownStage("choice");
    }
    setPhase("complete");
  };

  const next = () => {
    if (phase === "alignment") {
      setPhase(pendingOption?.name === "Sidekick" ? "sidekickOf" : "main");
    } else if (phase === "sidekickOf") {
      setPhase("main");
    } else if (phase === "main") {
      advanceMainIndex();
    } else if (phase === "abilities") {
      if (abilityStep === "count") {
        setAbilityStep(abilityCount > 0 ? "category" : "consolationGate");
      } else if (abilityStep === "consolationGate") {
        if (pendingOption?.name === "Yes") {
          setAbilityStep("consolationWeapon");
        } else {
          finishAbilities();
        }
      } else if (abilityStep === "consolationWeapon") {
        setAbilityStep("mastery");
      } else if (abilityStep === "category") {
        setAbilityStep("ability");
      } else if (abilityStep === "ability") {
        const completed = abilitiesCompleted + 1;
        setAbilitiesCompleted(completed);
        setCurrentAbilityCategory(null);
        setAbilityStep(completed < abilityCount ? "category" : "mastery");
      } else if (abilityStep === "mastery") {
        finishAbilities();
      }
    } else if (phase === "fight") {
      if (fightStep === "opponent") {
        setFightStep("outcome");
      } else {
        setFightStep(null);
        setPhase("complete");
      }
    }
    setPendingOption(null);
    setShowResult(false);
  };

  // Crown Mechanic: skip the bonus spin and keep the crown badge.
  const takeCrown = () => {
    setCrownStage(null);
  };

  // Crown Mechanic: spin the double-or-nothing wheel.
  const spinCrownWheel = () => {
    const option = spinWheel(CROWN_WHEEL);
    setCrownPendingOption(option);
    setCrownStage("spinning");
    setCrownSpinToken((t) => t + 1);
  };

  const handleCrownSpinEnd = () => {
    if (!crownPendingOption) return;
    const statName = CROWN_STAT_MAP[crownPendingOption.name];
    let bonus = crownPendingOption.value;
    if (statName) {
      const statResult = results.find((r) => r.wheelName === statName);
      bonus = statResult ? statResult.option.value : 0;
    } else if (crownPendingOption.name === "2x Best Ability") {
      const abilityResults = results.filter((r) =>
        r.wheelName.startsWith("Ability:"),
      );
      const best = abilityResults.reduce<SpinResult | null>(
        (max, r) => (!max || r.option.value > max.option.value ? r : max),
        null,
      );
      bonus = best ? best.option.value : 0;
    }
    const bonusOption: WheelOption = { name: crownPendingOption.name, value: bonus };
    setResults((prev) => [...prev, { wheelName: "Crown Bonus", option: bonusOption }]);
    setCrownBonusOption(bonusOption);
    setCrownStage("result");
  };

  const continueAfterCrown = () => {
    setCrownStage(null);
  };

  // Optional bonus round, started from the results screen.
  const startFight = () => {
    setFightResults([]);
    setFightStep("opponent");
    setPendingOption(null);
    setShowResult(false);
    setPhase("fight");
  };

  const reset = () => {
    resetState();
    clearActiveRun(saveKey);
    if (!ephemeral && user) clearActiveRunRemote(user.id).catch(() => {});
  };

  // Paid single-wheel reroll (results screen): re-spins ONE main-wheel result
  // in place and replaces its value, leaving the rest of the build untouched.
  // It's a clean re-spin (no chaos interaction) and the total score recomputes
  // automatically from `results`. Guarded by the per-run cap; the caller
  // (ResultsSummary) charges tokens before invoking this.
  const rerollWheel = (index: number) => {
    if (wheelRerollsUsed >= MAX_SINGLE_WHEEL_REROLLS) return;
    const entry = results[index];
    if (!entry) return;
    const wheel = WHEELS.find((w) => w.name === entry.wheelName);
    if (!wheel) return; // not a re-spinnable main wheel
    const option = spinWheel(wheel);
    setResults((prev) =>
      prev.map((r, i) => (i === index ? { wheelName: r.wheelName, option } : r)),
    );
    setWheelRerollsUsed((n) => n + 1);
  };

  const totalScore = calculateTotalScore(
    [...results, ...chaosResults, ...fightResults].map((r) => r.option),
  );
  const rank = calculateRank(totalScore);

  // Consecutive trailing positive rolls = a "hot streak" that sets the wheel on
  // fire. Derived straight from results (no extra state); a zero/negative roll
  // (e.g. Weakness) breaks it. Only while actively rolling the main/ability
  // wheels — never on cosmetic spins or the results screen.
  let positiveStreak = 0;
  for (let i = results.length - 1; i >= 0; i--) {
    if (results[i].option.value > 0) positiveStreak++;
    else break;
  }
  const heatLevel =
    phase !== "main" && phase !== "abilities"
      ? 0
      : positiveStreak >= 8
        ? 3
        : positiveStreak >= 5
          ? 2
          : positiveStreak >= 3
            ? 1
            : 0;

  // Just the Buff / Nerf chaos events, in the order they landed — for the
  // persistent on-wheel effects panel. Lore / Achievement events are excluded
  // (they're pure point bumps with no lingering effect to surface).
  const chaosEffects = chaosGameState.chaosEvents.filter(
    (e) => e.type === "Buff" || e.type === "Nerf",
  );

  const isSidekick = sidekickOf !== null;
  const effectiveAlignment = sidekickOf ?? alignment;
  const alignmentLabel = isSidekick
    ? `${sidekickOf}'s Sidekick`
    : alignment;
  const leaderboardBoard = getLeaderboardBoard(effectiveAlignment);

  // Dynamic wheel count: base wheels + sidekick follow-up + the ability
  // sequence (count + mastery + a category/ability pair per ability).
  const sidekickOffset = isSidekick ? 1 : 0;
  const abilityWheelCount =
    abilityCount === 0
      ? abilityStep === "consolationWeapon" || abilityStep === "mastery"
        ? 4
        : 2
      : 2 + abilityCount * 2;
  const mainWheelsTotal = WHEELS.length + sidekickOffset;
  const totalWheels =
    phase === "fight" ? 2 : mainWheelsTotal + abilityWheelCount;

  let wheelNumber = 0;
  if (phase === "sidekickOf") {
    wheelNumber = 1;
  } else if (phase === "main") {
    wheelNumber = sidekickOffset + (currentIndex + 1);
  } else if (phase === "abilities") {
    if (abilityStep === "count") {
      wheelNumber = mainWheelsTotal + 1;
    } else if (abilityStep === "consolationGate") {
      wheelNumber = mainWheelsTotal + 2;
    } else if (abilityStep === "consolationWeapon") {
      wheelNumber = mainWheelsTotal + 3;
    } else if (abilityStep === "category") {
      wheelNumber = mainWheelsTotal + 1 + abilitiesCompleted * 2 + 1;
    } else if (abilityStep === "ability") {
      wheelNumber = mainWheelsTotal + 1 + abilitiesCompleted * 2 + 2;
    } else {
      // mastery
      wheelNumber =
        abilityCount === 0
          ? mainWheelsTotal + 4
          : mainWheelsTotal + 1 + abilityCount * 2 + 1;
    }
  } else if (phase === "fight") {
    wheelNumber = fightStep === "outcome" ? 2 : 1;
  }

  const isLastStep =
    (phase === "abilities" && abilityStep === "mastery") ||
    (phase === "abilities" &&
      abilityStep === "consolationGate" &&
      pendingOption?.name === "No");

  // Whether there's a run in progress to resume (vs. a brand-new game).
  const hasActiveRun =
    phase !== "alignment" || alignment !== null || results.length > 0;

  return {
    hydrated,
    hasActiveRun,
    phase,
    currentWheel,
    showValue,
    wheelNumber,
    totalWheels,
    isLastStep,
    results,
    pendingOption,
    spinning,
    showResult,
    spinToken,
    spin,
    handleSpinEnd,
    next,
    reset,
    totalScore,
    rank,
    alignment,
    sidekickOf,
    isSidekick,
    alignmentLabel,
    leaderboardBoard,
    chaosResults,
    chaosGameState,
    chaosEffects,
    chaosCooldownLabel,
    canTriggerChaos,
    chaosPending,
    chaosOutcome,
    chaosOverlayStage,
    chaosTypeSpinToken,
    chaosResultSpinToken,
    triggerChaosManually,
    handleChaosTypeSpinEnd,
    handleChaosResultSpinEnd,
    dismissChaosOverlay,
    fightStep,
    fightResults,
    startFight,
    hasCrown,
    crownStage,
    crownSpinToken,
    crownPendingOption,
    crownBonusOption,
    takeCrown,
    spinCrownWheel,
    handleCrownSpinEnd,
    continueAfterCrown,
    showRestoredToast,
    dismissRestoredToast,
    markSubmitted,
    submittedScoreId,
    cooldownStartedAt,
    rerollWheel,
    wheelRerollsUsed,
    heatLevel,
    positiveStreak,
  };
}
